import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Camera, QrCode, Upload, Users, Sparkles, ArrowRight, Search, ImageIcon, Check, Instagram, Handshake, PartyPopper, Heart, Star, Lock, Mic, Building2, Briefcase } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import axios from "axios";
import { useLanguage } from "@/i18n/LanguageContext";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LandingPage() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState("");
  const [heroImages, setHeroImages] = useState([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [totalPhotos, setTotalPhotos] = useState(0);
  const [showCounter, setShowCounter] = useState(true);

  // Fetch total photos with real-time updates
  const fetchTotalPhotos = async () => {
    try {
      const response = await axios.get(`${API}/admin/stats`);
      setTotalPhotos(response.data.total_photos || 0);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    const fetchHeroImages = async () => {
      try {
        const response = await axios.get(`${API}/settings/hero-image`);
        if (response.data.images && response.data.images.length > 0) {
          setHeroImages(response.data.images.map(img => `${process.env.REACT_APP_BACKEND_URL}${img}`));
        }
      } catch (error) {
        console.error(error);
      }
    };
    
    const fetchShowCounter = async () => {
      try {
        const response = await axios.get(`${API}/settings/show-counter`);
        setShowCounter(response.data.show);
      } catch (error) {
        console.error(error);
      }
    };
    
    fetchHeroImages();
    fetchShowCounter();
    fetchTotalPhotos();
    
    // Real-time updates every 5 seconds
    const interval = setInterval(fetchTotalPhotos, 5000);
    return () => clearInterval(interval);
  }, []);

  // Carousel auto-rotation
  useEffect(() => {
    if (heroImages.length <= 1) return;
    
    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % heroImages.length);
    }, 4000);
    
    return () => clearInterval(interval);
  }, [heroImages.length]);

  const handleSearch = async (e) => {
    e.preventDefault();
    const code = searchQuery.trim().toLowerCase();
    
    if (!code || code.length < 2) {
      toast.error(t('minSearchChars'));
      return;
    }

    setIsSearching(true);
    setSearchError("");
    
    try {
      const response = await axios.get(`${API}/albums/search`, {
        params: { q: code }
      });
      
      if (response.data.found && response.data.album) {
        // Album found - redirect to upload page for guests
        navigate(`/upload/${response.data.album.share_code}`);
      } else {
        // Album not found - show error
        setSearchError(t('albumNotFoundError'));
        toast.error(t('albumNotFoundError'));
      }
    } catch (error) {
      setSearchError(t('albumNotFoundError'));
      toast.error(t('searchFailed'));
      console.error(error);
    } finally {
      setIsSearching(false);
    }
  };

  const steps = [
    {
      icon: <QrCode className="w-6 h-6" />,
      title: t('step1Title'),
      description: t('step1Desc')
    },
    {
      icon: <Upload className="w-6 h-6" />,
      title: t('step2Title'),
      description: t('step2Desc')
    },
    {
      icon: <Sparkles className="w-6 h-6" />,
      title: t('step3Title'),
      description: t('step3Desc')
    }
  ];

  return (
    <div className="min-h-screen bg-white" data-testid="landing-page">
      {/* Navigation */}
      <nav className="fixed top-0 w-full z-40 bg-white/80 backdrop-blur-md border-b border-slate-200/50 h-16 flex items-center px-4 md:px-8">
        <div className="flex items-center justify-between w-full max-w-6xl mx-auto">
          <div className="flex items-center gap-2">
            <img src="/mf-logo.png" alt="MFcreativemanager" className="w-8 h-8 object-contain" />
            <span className="font-bold text-lg sm:text-xl text-slate-900 hidden sm:block" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {t('appName')}
            </span>
          </div>
          <div className="flex items-center gap-3">
            <a
              href="https://www.instagram.com/mfcreativemanager"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500 text-white text-sm font-medium hover:opacity-90 transition-opacity"
              data-testid="instagram-link-nav"
            >
              <Instagram className="w-4 h-4" />
              <span className="hidden sm:inline">Instagram</span>
            </a>
            <LanguageSwitcher />
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 md:px-8 lg:px-12 hero-pattern">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left - Text Content */}
            <div className="space-y-8">
              <div className="space-y-4">
                <h1 
                  className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-slate-900 tracking-tight leading-tight"
                  style={{ fontFamily: 'Manrope, sans-serif' }}
                >
                  {t('heroTitle')}{" "}
                  <span className="gradient-text">{t('heroTitleAccent')}</span>
                </h1>
                <p className="text-lg text-slate-600 max-w-lg">
                  {t('heroSubtitle')}
                </p>
              </div>

              {/* Search Album Section */}
              <div className="space-y-4 max-w-md">
                <form onSubmit={handleSearch} className="space-y-3">
                  <label 
                    className="text-sm font-semibold text-slate-700 uppercase tracking-wider block"
                    htmlFor="searchQuery"
                  >
                    {t('findTheAlbum')}
                  </label>
                  <div className="flex gap-2">
                    <Input
                      id="searchQuery"
                      data-testid="search-album-input"
                      placeholder={t('enterEventCodeOrName')}
                      value={searchQuery}
                      onChange={(e) => {
                        setSearchQuery(e.target.value);
                        setSearchError("");
                      }}
                      className={`h-14 rounded-xl border-slate-200 bg-slate-50 focus:bg-white focus:ring-2 focus:ring-indigo-500/20 transition-all text-base flex-1 ${searchError ? 'border-red-300 bg-red-50' : ''}`}
                    />
                    <Button
                      type="submit"
                      data-testid="search-album-btn"
                      disabled={isSearching}
                      className="h-14 px-6 rounded-xl bg-gradient-to-r from-indigo-600 to-pink-600 text-white hover:opacity-90"
                    >
                      {isSearching ? (
                        <div className="spinner" />
                      ) : (
                        <Search className="w-5 h-5" />
                      )}
                    </Button>
                  </div>
                  
                  {/* Error Message */}
                  {searchError && (
                    <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm" data-testid="search-error">
                      {searchError}
                    </div>
                  )}
                </form>
                
                {/* Client Access Button - Aligned left */}
                <div className="mt-6 text-left">
                  <button
                    onClick={() => navigate('/client')}
                    className="inline-flex items-center gap-2 text-base font-bold bg-gradient-to-r from-indigo-600 to-pink-600 bg-clip-text text-transparent hover:opacity-80 transition-opacity"
                    data-testid="client-access-btn"
                  >
                    <Lock className="w-5 h-5 text-indigo-600" />
                    {t('imTheHost') || '¿Eres el anfitrión? Accede aquí'}
                  </button>
                </div>
              </div>
            </div>

            {/* Right - Hero Image Carousel */}
            {heroImages.length > 0 && (
              <div className="relative">
                {/* Mobile version - smaller */}
                <div className="lg:hidden flex justify-center mb-6">
                  <div className="relative rounded-2xl overflow-hidden shadow-xl w-64 h-64 sm:w-80 sm:h-80">
                    {heroImages.map((img, index) => (
                      <img
                        key={index}
                        src={img}
                        alt={`Carousel ${index + 1}`}
                        className={`w-full h-full object-cover transition-opacity duration-500 ${
                          index === currentImageIndex ? 'opacity-100' : 'opacity-0 absolute inset-0'
                        }`}
                      />
                    ))}
                    {/* Mobile Carousel Indicators */}
                    {heroImages.length > 1 && (
                      <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-2">
                        {heroImages.map((_, index) => (
                          <button
                            key={index}
                            onClick={() => setCurrentImageIndex(index)}
                            className={`w-2 h-2 rounded-full transition-all ${
                              index === currentImageIndex 
                                ? 'bg-white w-5' 
                                : 'bg-white/50 hover:bg-white/70'
                            }`}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Desktop version */}
                <div className="hidden lg:block">
                  <div className="relative rounded-3xl overflow-hidden shadow-2xl transform rotate-2 hover:rotate-0 transition-transform duration-500">
                    {heroImages.map((img, index) => (
                      <img
                        key={index}
                        src={img}
                        alt={`Carousel ${index + 1}`}
                        className={`w-full h-auto object-cover transition-opacity duration-500 ${
                          index === currentImageIndex ? 'opacity-100' : 'opacity-0 absolute inset-0'
                        }`}
                      />
                    ))}
                  </div>
                  
                  {/* Desktop Carousel Indicators */}
                  {heroImages.length > 1 && (
                    <div className="absolute -bottom-8 left-1/2 -translate-x-1/2 flex gap-2">
                      {heroImages.map((_, index) => (
                        <button
                          key={index}
                          onClick={() => setCurrentImageIndex(index)}
                          className={`w-2 h-2 rounded-full transition-all ${
                            index === currentImageIndex 
                              ? 'bg-indigo-600 w-6' 
                              : 'bg-slate-300 hover:bg-slate-400'
                          }`}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Photo Counter - Always visible if enabled */}
          {showCounter && (
            <div className="mt-8 flex justify-center lg:justify-start">
              <div className="bg-white rounded-2xl p-4 shadow-xl flex items-center gap-3 animate-fade-in-up border border-slate-100">
                <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                  <Users className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <p className="font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="total-photos-counter">
                    {totalPhotos} {t('photos')}
                  </p>
                  <p className="text-sm text-slate-500">{t('photosUploaded')}</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 px-4 md:px-8 lg:px-12 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 
              className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              {t('howItWorks')}
            </h2>
            <p className="mt-4 text-lg text-slate-600">
              {t('howItWorksSubtitle')}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {steps.map((step, index) => (
              <div
                key={index}
                className="bg-white border border-slate-100 rounded-2xl p-8 shadow-sm hover:shadow-md transition-shadow group"
                data-testid={`step-${index + 1}`}
              >
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-indigo-100 to-pink-100 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <div className="text-indigo-600">
                    {step.icon}
                  </div>
                </div>
                <div className="text-sm font-semibold text-indigo-600 uppercase tracking-wider mb-2">
                  {t('step')} {index + 1}
                </div>
                <h3 
                  className="text-xl font-bold text-slate-900 mb-2"
                  style={{ fontFamily: 'Manrope, sans-serif' }}
                >
                  {step.title}
                </h3>
                <p className="text-slate-600">
                  {step.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Any Event Section - Fun and inviting */}
      <section className="py-20 px-4 md:px-8 lg:px-12 bg-indigo-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-2 bg-indigo-100 text-indigo-700 text-sm font-semibold rounded-full mb-6">
              🎉 {t('anyEventSectionLabel')}
            </span>
            <h2 
              className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight mb-4"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              {t('anyEventTitle')}
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              {t('anyEventSubtitle')}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 lg:gap-8 mb-12">
            {/* Conferences */}
            <div className="bg-white rounded-2xl p-6 lg:p-8 shadow-md hover:shadow-xl transition-all group border border-slate-100">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-purple-100 to-indigo-100 flex items-center justify-center mb-5 group-hover:scale-110 transition-transform">
                <Mic className="w-7 h-7 text-indigo-600" />
              </div>
              <h3 
                className="text-xl font-bold text-slate-900 mb-3"
                style={{ fontFamily: 'Manrope, sans-serif' }}
              >
                {t('conferenceTitle')}
              </h3>
              <p className="text-slate-600 leading-relaxed">
                {t('conferenceDesc')}
              </p>
            </div>

            {/* Expositions */}
            <div className="bg-white rounded-2xl p-6 lg:p-8 shadow-md hover:shadow-xl transition-all group border border-slate-100">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-pink-100 to-rose-100 flex items-center justify-center mb-5 group-hover:scale-110 transition-transform">
                <Building2 className="w-7 h-7 text-pink-600" />
              </div>
              <h3 
                className="text-xl font-bold text-slate-900 mb-3"
                style={{ fontFamily: 'Manrope, sans-serif' }}
              >
                {t('expoTitle')}
              </h3>
              <p className="text-slate-600 leading-relaxed">
                {t('expoDesc')}
              </p>
            </div>

            {/* Corporate */}
            <div className="bg-white rounded-2xl p-6 lg:p-8 shadow-md hover:shadow-xl transition-all group border border-slate-100">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-amber-100 to-orange-100 flex items-center justify-center mb-5 group-hover:scale-110 transition-transform">
                <Briefcase className="w-7 h-7 text-amber-600" />
              </div>
              <h3 
                className="text-xl font-bold text-slate-900 mb-3"
                style={{ fontFamily: 'Manrope, sans-serif' }}
              >
                {t('corporateTitle')}
              </h3>
              <p className="text-slate-600 leading-relaxed">
                {t('corporateDesc')}
              </p>
            </div>
          </div>

          {/* CTA */}
          <div className="text-center">
            <p className="text-lg text-slate-700 font-medium bg-white inline-block px-8 py-4 rounded-full shadow-sm border border-slate-200">
              ✨ {t('anyEventCTA')}
            </p>
          </div>
        </div>
      </section>

      {/* Album Plans Section */}
      <section className="py-20 px-4 md:px-8 lg:px-12 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 
              className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              {t('albumPlans')}
            </h2>
            <p className="mt-4 text-lg text-slate-600">
              {t('albumPlansSubtitle')}
            </p>
          </div>

          <div className="grid sm:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {/* Basic Plan - 100 */}
            <div className="bg-white border border-slate-200 rounded-2xl p-8 hover:border-indigo-300 hover:shadow-lg transition-all group">
              <div className="text-center">
                <div className="w-16 h-16 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-4 group-hover:bg-indigo-100 transition-colors">
                  <ImageIcon className="w-8 h-8 text-slate-600 group-hover:text-indigo-600" />
                </div>
                <h3 
                  className="text-xl font-bold text-slate-900 mb-2"
                  style={{ fontFamily: 'Manrope, sans-serif' }}
                >
                  {t('basicPlan')}
                </h3>
                <p className="text-4xl font-extrabold text-slate-900 mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  100
                </p>
                <p className="text-slate-500 text-sm mb-4">{t('photosLimit')}</p>
                <div className="flex items-center justify-center gap-2 text-sm text-slate-600">
                  <Check className="w-4 h-4 text-green-500" />
                  <span>{t('perfectFor')} {t('smallEvents')}</span>
                </div>
              </div>
            </div>

            {/* Standard Plan - 300 */}
            <div className="bg-white border-2 border-indigo-500 rounded-2xl p-8 hover:shadow-lg transition-all group relative">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-gradient-to-r from-indigo-600 to-pink-600 text-white text-xs font-bold rounded-full">
                POPULAR
              </div>
              <div className="text-center">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-indigo-100 to-pink-100 flex items-center justify-center mx-auto mb-4">
                  <ImageIcon className="w-8 h-8 text-indigo-600" />
                </div>
                <h3 
                  className="text-xl font-bold text-slate-900 mb-2"
                  style={{ fontFamily: 'Manrope, sans-serif' }}
                >
                  {t('standardPlan')}
                </h3>
                <p className="text-4xl font-extrabold text-indigo-600 mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  300
                </p>
                <p className="text-slate-500 text-sm mb-4">{t('photosLimit')}</p>
                <div className="flex items-center justify-center gap-2 text-sm text-slate-600">
                  <Check className="w-4 h-4 text-green-500" />
                  <span>{t('perfectFor')} {t('mediumEvents')}</span>
                </div>
              </div>
            </div>

            {/* Unlimited Plan */}
            <div className="bg-gradient-to-br from-slate-900 to-slate-800 border border-slate-700 rounded-2xl p-8 hover:shadow-lg transition-all group">
              <div className="text-center">
                <div className="w-16 h-16 rounded-2xl bg-white/10 flex items-center justify-center mx-auto mb-4">
                  <Sparkles className="w-8 h-8 text-white" />
                </div>
                <h3 
                  className="text-xl font-bold text-white mb-2"
                  style={{ fontFamily: 'Manrope, sans-serif' }}
                >
                  {t('unlimitedPlan')}
                </h3>
                <p className="text-4xl font-extrabold text-white mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  ∞
                </p>
                <p className="text-slate-400 text-sm mb-4">{t('photosLimit')}</p>
                <div className="flex items-center justify-center gap-2 text-sm text-slate-300">
                  <Check className="w-4 h-4 text-green-400" />
                  <span>{t('perfectFor')} {t('anyEvent')}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Instagram CTA */}
          <div className="mt-12 text-center">
            <a
              href="https://www.instagram.com/mfcreativemanager"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-indigo-600 to-pink-600 text-white font-semibold rounded-full hover:opacity-90 transition-all shadow-lg hover:shadow-xl hover:scale-105"
              data-testid="instagram-plan-cta"
            >
              <Instagram className="w-5 h-5" />
              <span style={{ fontFamily: 'Manrope, sans-serif' }}>{t('contactForPlan')}</span>
            </a>
          </div>
        </div>
      </section>

      {/* Alianzas / Partnerships Section */}
      <section className="py-16 px-4 md:px-8 relative overflow-hidden" data-testid="partnerships-section">
        {/* Background decorations */}
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-50 via-pink-50 to-purple-50" />
        <div className="absolute top-0 left-0 w-64 h-64 bg-gradient-to-br from-indigo-400/20 to-pink-400/20 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2" />
        <div className="absolute bottom-0 right-0 w-80 h-80 bg-gradient-to-br from-purple-400/20 to-indigo-400/20 rounded-full blur-3xl translate-x-1/2 translate-y-1/2" />
        
        <div className="max-w-6xl mx-auto relative">
          {/* Header */}
          <div className="text-center mb-12">
            <p className="text-sm font-semibold text-indigo-600 uppercase tracking-wider mb-4">
              {t('partnershipsLabel')}
            </p>
            <h2 
              className="text-3xl md:text-4xl font-extrabold text-slate-900 mb-4"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              {t('partnershipsTitle')}
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto text-lg">
              {t('partnershipsSubtitle')}
            </p>
          </div>

          {/* Partnership cards */}
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* Event Planners Card */}
            <div className="bg-white rounded-3xl p-8 shadow-xl hover:shadow-2xl transition-all border border-slate-100 group relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-indigo-100 to-pink-100 rounded-full blur-2xl opacity-50 group-hover:opacity-100 transition-opacity" />
              <div className="relative">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center mb-6 shadow-lg group-hover:scale-110 transition-transform">
                  <PartyPopper className="w-8 h-8 text-white" />
                </div>
                <h3 
                  className="text-2xl font-bold text-slate-900 mb-3"
                  style={{ fontFamily: 'Manrope, sans-serif' }}
                >
                  {t('eventPlannersTitle')}
                </h3>
                <p className="text-slate-600 leading-relaxed">
                  {t('eventPlannersDesc')}
                </p>
              </div>
            </div>

            {/* Decorators Card */}
            <div className="bg-white rounded-3xl p-8 shadow-xl hover:shadow-2xl transition-all border border-slate-100 group relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-pink-100 to-purple-100 rounded-full blur-2xl opacity-50 group-hover:opacity-100 transition-opacity" />
              <div className="relative">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-pink-500 to-rose-600 flex items-center justify-center mb-6 shadow-lg group-hover:scale-110 transition-transform">
                  <Heart className="w-8 h-8 text-white" />
                </div>
                <h3 
                  className="text-2xl font-bold text-slate-900 mb-3"
                  style={{ fontFamily: 'Manrope, sans-serif' }}
                >
                  {t('decoratorsTitle')}
                </h3>
                <p className="text-slate-600 leading-relaxed">
                  {t('decoratorsDesc')}
                </p>
              </div>
            </div>
          </div>

          {/* Special Pricing Message */}
          <div className="text-center mt-10">
            <p className="text-xl font-bold bg-gradient-to-r from-indigo-600 to-pink-600 bg-clip-text text-transparent">
              {t('specialPricing')}
            </p>
          </div>

          {/* CTA Button */}
          <div className="text-center mt-12">
            <a
              href="https://www.instagram.com/mfcreativemanager"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full border-2 border-indigo-600 text-indigo-600 font-semibold hover:bg-indigo-50 transition-all"
              data-testid="partnership-cta"
            >
              <Handshake className="w-5 h-5" />
              <span>{t('partnershipCTA')}</span>
              <ArrowRight className="w-4 h-4" />
            </a>
            <p className="text-slate-500 mt-4 text-sm">
              {t('partnershipNote')}
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 md:px-8 border-t border-slate-100">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <img src="/mf-logo.png" alt="MFcreativemanager" className="w-6 h-6 object-contain" />
              <span className="font-semibold text-slate-700" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {t('appName')}
              </span>
            </div>
            
            {/* Instagram Contact Link */}
            <a
              href="https://www.instagram.com/mfcreativemanager"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500 text-white font-semibold hover:opacity-90 transition-opacity shadow-lg hover:shadow-xl"
              data-testid="instagram-link"
            >
              <Instagram className="w-5 h-5" />
              <span>{t('contactUsOnInstagram')}</span>
            </a>
            
            <p className="text-sm text-slate-500">
              {t('appTagline')}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
